import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { StatusBadge, getLeadStatusVariant } from '@/components/common/StatusBadge';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { LeadSearchResultItem, LeadSearchResult } from '../types/leadSearch';

interface LeadSearchResultsProps {
  results: LeadSearchResult<LeadSearchResultItem> | null;
  isLoading: boolean;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export function LeadSearchResults({
  results,
  isLoading,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: LeadSearchResultsProps) {
  const navigate = useNavigate();

  const handleRowClick = (leadId: number) => {
    navigate(`/leads/${leadId}`);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-[20px] p-6">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D71F20] mb-4"></div>
          <p className="text-[#84878A]">Searching leads...</p>
        </div>
      </div>
    );
  }

  if (!results) {
    return null;
  }

  if (results.leads.length === 0) {
    return (
      <div className="bg-white rounded-[20px] p-6">
        <div className="flex flex-col items-center justify-center py-12">
          <p className="text-lg text-[#84878A]">No leads found matching your criteria</p>
          <p className="text-sm text-[#84878A] mt-2">Try adjusting your search filters</p>
        </div>
      </div>
    );
  }

  const startIndex = (results.page - 1) * results.pageSize + 1;
  const endIndex = Math.min(results.page * results.pageSize, results.totalCount);

  return (
    <div className="bg-white rounded-[20px] p-6 space-y-4">
      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#84878A]">
          Showing {startIndex}-{endIndex} of {results.totalCount} results
        </p>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#84878A]">Results per page:</span>
          <Select
            value={pageSize.toString()}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="h-9 w-20"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Select>
        </div>
      </div>

      {/* Results Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Job Number</TableHead>
              <TableHead>Customer Name</TableHead>
              <TableHead>Property Address</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Lead Date</TableHead>
              <TableHead>Assigned To</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {results.leads.map((lead) => (
              <TableRow
                key={lead.id}
                onClick={() => handleRowClick(lead.id)}
                className="cursor-pointer hover:bg-gray-50"
              >
                <TableCell className="font-medium">
                  {lead.jobNumber || 'N/A'}
                </TableCell>
                <TableCell>{lead.customerName}</TableCell>
                <TableCell>{lead.propertyAddress}</TableCell>
                <TableCell>
                  <StatusBadge variant={getLeadStatusVariant(lead.status)}>
                    {lead.status}
                  </StatusBadge>
                </TableCell>
                <TableCell>
                  {new Date(lead.createdAt).toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                  })}
                </TableCell>
                <TableCell>{lead.assignedToUserName || 'Unassigned'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => onPageChange(results.page - 1)}
          disabled={results.page === 1}
          className="flex items-center gap-2"
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#84878A]">
            Page {results.page} of {results.totalPages}
          </span>
        </div>
        <Button
          variant="outline"
          onClick={() => onPageChange(results.page + 1)}
          disabled={results.page >= results.totalPages}
          className="flex items-center gap-2"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
